import { useRef } from 'react'
import { useTodos } from '../../api/hooks'
import { useUIStore } from '../../store/ui'
import { sortTodos } from '../../lib/todoSort'
import { formatDate, todayStr } from '../../lib/parse'
import { useSwipe } from '../../hooks/useSwipe'
import { TodoInput } from '../TodoInput'
import { MobileDayHeader } from './MobileDayHeader'
import { MobileTodoItem } from './MobileTodoItem'

function addDays(dateStr: string, delta: number): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  date.setDate(date.getDate() + delta)
  return formatDate(date)
}

export function MobileDayView() {
  const focusedDate = useUIStore((s) => s.focusedDate)
  const setFocusedDate = useUIStore((s) => s.setFocusedDate)
  const smartStatusSort = useUIStore((s) => s.smartStatusSort)

  const { data: todos = [], isLoading } = useTodos({ from: focusedDate, to: focusedDate })
  const sorted = sortTodos(todos, smartStatusSort)

  const isToday = focusedDate === todayStr()

  function goPrevDay() {
    setFocusedDate(addDays(focusedDate, -1))
  }
  function goNextDay() {
    setFocusedDate(addDays(focusedDate, 1))
  }

  // Swipe on the scroll container but not on todo rows.
  const { bind: swipeBind } = useSwipe({
    onSwipeLeft: goNextDay,
    onSwipeRight: goPrevDay,
    threshold: 60,
    shouldStart: (e: PointerEvent) => {
      if (e.target instanceof Element && e.target.closest('[data-todo-row]')) return false
      return true
    },
  })

  const scrollRef = useRef<HTMLDivElement>(null)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MobileDayHeader
        dateStr={focusedDate}
        onPrev={goPrevDay}
        onNext={goNextDay}
        onJumpToToday={() => setFocusedDate(todayStr())}
        isToday={isToday}
      />

      <div
        ref={scrollRef}
        {...swipeBind}
        style={{ flex: 1, overflowY: 'auto', touchAction: 'pan-y' }}
      >
        {isLoading ? (
          <div
            style={{
              textAlign: 'center',
              padding: '32px 16px',
              fontFamily: 'var(--font-serif)',
              fontStyle: 'italic',
              color: 'var(--color-ink-faint)',
            }}
          >
            loading…
          </div>
        ) : sorted.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '32px 16px',
              fontFamily: 'var(--font-serif)',
              fontStyle: 'italic',
              color: 'var(--color-ink-faint)',
              fontSize: '0.875rem',
            }}
          >
            no todos for this day
          </div>
        ) : (
          sorted.map((t) => (
            <MobileTodoItem key={t.id} todo={t} />
          ))
        )}
      </div>

      <div
        style={{
          borderTop: '1px solid var(--color-border)',
          padding: '8px 16px',
          paddingBottom: 'calc(8px + env(safe-area-inset-bottom))',
          background: 'var(--color-paper)',
        }}
      >
        <TodoInput defaultDate={focusedDate} todos={todos} placeholder="+ add task" />
      </div>
    </div>
  )
}
